import "@progress/kendo-ui/js/kendo.datepicker";
import "@progress/kendo-ui/js/kendo.timepicker";
import * as ko from "knockout";
import * as _ from "lodash";
import { AddFormDialog } from "../common/addFormDialog";
import * as Constants from "../common/constants";
import * as FormDataEntry from "../common/formDataEntry";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import * as AppointmentData from "../data/appointmentData";
import * as TargetData from "../data/targetData";
import * as TemplateData from "../data/templateData";
import { IApplication } from "../interfaces/application";
import { IController } from "../interfaces/controller";
import { IResponse } from "../interfaces/data";
import { ILanguage, ITranslatedText } from "../interfaces/translations";

// ==================================================================
// interfaces
// ==================================================================

// data needed to load this control with form data
interface IListObservable {
    ID: KnockoutObservable<number>;
    NavigationID: KnockoutObservable<number>;
    ListName: KnockoutObservable<string>;
    HasForm: KnockoutObservable<boolean>;
    HasAttributes: KnockoutObservable<boolean>;
    SortOrder: KnockoutObservable<number>;
    CanDelete: KnockoutObservable<boolean>;
    IsActive: KnockoutObservable<boolean>;

    /// lists that are children of this list.  These are lists that get
    /// created automatically when an appointment is created from
    /// this template.
    Children: KnockoutObservableArray<IListObservable>;

    /// if this list item is a form, contains the fields for the form
    /// required for save when HasForm is true.
    ListForm: AppointmentData.IAppointmentFormItem[];

    /// these are forms that can be added to this list.
    ListItems: KnockoutObservableArray<AppointmentData.IAppointmentListItem>;

    /// if HasForm is true, this will contain the ID of the
    /// form associated with the list item.
    TemplateFormID: KnockoutObservable<number>;

    // if true, then any child items are visible
    IsExpanded: KnockoutObservable<boolean>;

    // if true, then this item is being edited
    IsSelected: KnockoutObservable<boolean>;

    // if true, show action buttons for this item
    ShowActions: KnockoutObservable<boolean>;

    typeIsForm?: KnockoutComputed<boolean>;
    typeIsList?: KnockoutComputed<boolean>;
    numberOfLists?: KnockoutComputed<number>;
    rowStyle?: KnockoutComputed<string>;
}

interface ILinkedCustomerDocument {
    CustomerDocumentID: number;
    IsActive: boolean;
    Description: string;
    FileName: string;
}

// represents a top level navigation item displayed in the tree
interface INavigationObservable {
    ID: KnockoutObservable<number>;
    TemplateFormID: KnockoutObservable<number>;
    NavigationTypeID: KnockoutObservable<number>;
    NavigationName: KnockoutComputed<string>;
    NavigationNameDefault: KnockoutObservable<string>;
    NavigationNameTranslations: ITranslatedText[];
    NavigationIcon: KnockoutObservable<string>;
    SortOrder: KnockoutObservable<number>;

    // used when a navigation item goes directly to a form (TemplateFormID is not null).
    // contains all of the fields on that form.
    // only used when navigation type is 'Form'
    Forms: AppointmentData.IAppointmentFormItem[];

    // forms that can added under the navigation item
    ListItems: KnockoutObservableArray<AppointmentData.IAppointmentListItem>;

    // lists that are under the navigation item
    Lists: KnockoutObservableArray<IListObservable>;

    // computed functions
    typeIsForm?: KnockoutComputed<boolean>;
    typeIsList?: KnockoutComputed<boolean>;
    typeIsReport?: KnockoutComputed<boolean>;
    typeIsFinishTask?: KnockoutComputed<boolean>;
    showFinishTask?: KnockoutComputed<boolean>;

    numberOfLists?: KnockoutComputed<number>;
    rowStyle?: KnockoutComputed<string>;

    // if true, then any child items are visible
    IsExpanded: KnockoutObservable<boolean>;

    // if true, then this item is being edited
    IsSelected: KnockoutObservable<boolean>;

    // if true, show action buttons for this item
    ShowActions: KnockoutObservable<boolean>;
}

// view model for showing the appointment tree structure
interface IViewModelTree {
    Navigations: KnockoutObservableArray<INavigationObservable>;
    onEditNavigation: (navigationObservable: INavigationObservable) => void;
    onShowNavigationReport: (navigationObservable: INavigationObservable) => void;
    onShowFinishAppointment: (navigationObservable: INavigationObservable) => void;
    onAddNavigationList: (navigationObservable: INavigationObservable) => void;
    onNavigationShowActions: (navigationObservable: INavigationObservable) => void;
    onNavigationHideActions: (navigationObservable: INavigationObservable) => void;
    onEditList: (listObservable: IListObservable) => void;
    onNavigationToggleExpanded: (navigationObservable: INavigationObservable) => void;
    onAddList: (listObservable: IListObservable) => void;
    onMoveListUp: (listObservable: IListObservable) => void;
    onMoveListDown: (listObservable: IListObservable) => void;
    onListToggleExpanded: (listObservable: IListObservable) => void;
    onListShowActions: (listObservable: IListObservable) => void;
    onListHideActions: (listObservable: IListObservable) => void;
}

// view model for editing appointment info like date, assigned user,etc.
interface IViewModelAppointmentDetail {
    TargetShortDescription: KnockoutObservable<string>;
    LinkedCustomerDocuments: KnockoutObservableArray<ILinkedCustomerDocument>;
    CustomerDocumentDownloadUrl: string;
    LanguageID: KnockoutObservable<number>;
    Languages: ILanguage[];
}

// view model for exception report
interface IViewModelExceptionReport {
    ExceptionNavigations: KnockoutObservableArray<AppointmentData.IAppointmentExceptionReportNavigation>;
    AppointmentLabel: KnockoutObservable<string>;
    QuestionLabel: KnockoutObservable<string>;
}

interface IAdditionalFormDialogViewModel {
    formItemIndex: KnockoutObservable<number>;
    field: KnockoutObservable<FormDataEntry.IFieldObservable | null>;
    hasExceptionForm: KnockoutObservable<boolean>;
    SaveProcessStarted: KnockoutObservable<boolean>;
    onSaveForm: () => void;
    onCancelForm: () => void;
}

interface IViewModelFinishAppointment {
    appointmentLabel: string;
    finishButtonLabel: string;
    isOpen: KnockoutObservable<boolean>;
    isCancelled: KnockoutObservable<boolean>;
    hasValidationErrors: KnockoutObservable<boolean>;
    navigationErrors: KnockoutObservableArray<AppointmentData.IAppointmentValidationErrors>;
    onFinishAppointment: () => void;
    onEditNavigationAfterFailedValidation: (model: AppointmentData.IAppointmentValidationErrors) => void;
    onCancelAppointment: () => void;
    onOpenAppointment: () => void;
}

// ==================================================================
// TaskEntry Controller
// ==================================================================
export class TaskEntryController implements IController {
    // -------------------------------------------------------------------------
    // class properties
    // -------------------------------------------------------------------------
    private container: JQuery;
    private viewModelTree: IViewModelTree;
    private additionalviewModel: IAdditionalFormDialogViewModel;
    private viewModelAppointmentDetail: IViewModelAppointmentDetail;
    private viewModelExceptionReport: IViewModelExceptionReport;
    private viewModelFinishAppointment: IViewModelFinishAppointment;
    private appointmentID: number;
    private navigationID: number;
    private listID: number | null;
    private templateFormID: number | null;
    private formDetail: boolean;
    private formControl: FormDataEntry.FormDataEntryControl;
    private additionalFormControl: FormDataEntry.FormDataEntryControl;
    private addFormDialog: AddFormDialog;

    // there are used to display additional navigation items
    // that are not part of the template.
    private NAVIGATIONTYPE_FINISH_TASK = -1;

    // field to determine if validation should be skipped for form answers
    private skipValidation: boolean = true;

    // -------------------------------------------------------------------------
    // constructor
    // -------------------------------------------------------------------------
    constructor() {
        const me: TaskEntryController = this;
        this.viewModelAppointmentDetail = this.createViewModelAppointmentDetail();
        this.viewModelTree = this.createViewModelTree();
        this.viewModelExceptionReport = this.createViewModelExceptionReport();
        this.viewModelFinishAppointment = this.createViewModelFinishAppointment();

        this.additionalFormControl = new FormDataEntry.FormDataEntryControl({});
        this.formControl = new FormDataEntry.FormDataEntryControl({
            openAdditionalForm(formItemIndex: number, hasExceptionForm: boolean, templateFormID: number, field?: FormDataEntry.IFieldObservable): void {
                me.additionalviewModel.formItemIndex(formItemIndex);
                if (field) {
                    me.additionalviewModel.field(field);
                } else {
                    me.additionalviewModel.field(null);
                }
                me.additionalviewModel.hasExceptionForm(hasExceptionForm);
                me.openAdditionalForm(templateFormID);
            },

        });

        this.additionalviewModel = {
            formItemIndex: ko.observable(0),
            field: ko.observable(null),
            hasExceptionForm: ko.observable(false),
            SaveProcessStarted: ko.observable(false),

            onSaveForm(): void {
                this.SaveProcessStarted(true);
                me.save(true).always(() => {
                    this.SaveProcessStarted(false);
                });

            },
            onCancelForm(): void {
                me.container.find("div[data-id='additionalFormDialog']").modal("hide");

                if (me.additionalviewModel.hasExceptionForm()) {
                    const myformitems: FormDataEntry.IFormItemsMaster = me.formControl.getFormItems();
                    if (myformitems.FormItems[me.additionalviewModel.formItemIndex()].ControlTypeID === Constants.QuestionType.DROPDOWN) {
                        myformitems.FormItems[me.additionalviewModel.formItemIndex()].dropdownselected = null;
                    } else if (myformitems.FormItems[me.additionalviewModel.formItemIndex()].ControlTypeID === Constants.QuestionType.RADIOBUTTONLIST) {
                        myformitems.FormItems[me.additionalviewModel.formItemIndex()].selected = "";
                    } else if (myformitems.FormItems[me.additionalviewModel.formItemIndex()].ControlTypeID === Constants.QuestionType.CHECKBOXLIST) {
                        const selectedArray = myformitems.FormItems[me.additionalviewModel.formItemIndex()].selectedArray;
                        const field = me.additionalviewModel.field();
                        if (selectedArray && field) {
                            selectedArray.splice(selectedArray.indexOf(field.FieldValue()), 1);
                        }
                    }
                }
            },
        };

        this.addFormDialog = new AddFormDialog();
    }

    // -------------------------------------------------------------------------
    // iController implementation
    // -------------------------------------------------------------------------
    public save(isAdditionalForm: boolean = false): JQueryPromise<any> {

        const me: TaskEntryController = this;
        let waitContainer: JQuery;
        const deferred: JQuery.Deferred<any> = $.Deferred();
        waitContainer = UI.showWaitIndicator(me.container);

        if (isAdditionalForm) {
            // get OrigAppointmentFormItemID to save additional form answers
            const myformitems = me.formControl.getFormItems();
            const originalFormItemID: number = myformitems.FormItems[me.additionalviewModel.formItemIndex()].ID;
            AppointmentData.SaveAppointmentAdditionalForm(ko.toJS(me.additionalFormControl.getFormItemsForSave()),
                this.navigationID, this.listID, originalFormItemID)
                .done((response: IResponse<AppointmentData.ISaveAppointmentResponse>) => {
                    if (response.Success) {
                        me.container.find("div[data-id='additionalFormDialog']").modal("hide");
                        UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);

                        deferred.resolve();
                    } else {
                        UI.showErrorMessage(response.ErrorMessages);
                        deferred.reject();
                    }
                })
                .fail(() => {
                    deferred.reject();
                })
                .always(() => {
                    UI.hideWaitIndicator(me.container, waitContainer);
                });

        } else {
            AppointmentData.SaveAppointmentForm(ko.toJS(me.formControl.getFormItemsForSave()), this.navigationID, this.skipValidation, this.listID)
                .done((response: IResponse<AppointmentData.ISaveAppointmentResponse>): void => {
                    if (response.Success) {
                        UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                        me.loadFormItemsByTemplateFormID(me.templateFormID, me.appointmentID).done(() => {
                            deferred.resolve();
                        })
                        .fail(() => {
                            deferred.reject();
                        });
                    } else {
                        UI.showErrorMessage(response.ErrorMessages);
                        deferred.reject();
                    }
                })
                .fail(() => {
                    deferred.reject();
                })
                .always(() => {
                    UI.hideWaitIndicator(me.container, waitContainer);
                });
        }

        return deferred.promise();
    }

    public render(container: JQuery): JQueryPromise<any> {
        const me: TaskEntryController = this;
        const deferred: JQuery.Deferred<any> = $.Deferred();

        me.container = container;
        me.formControl.render(container.find("div[data-id='formContainer']").find(".tree-detail-container")).done(() => {
            me.additionalFormControl.render(container.find("div[data-id='additionalFormContainer']")).done(() => {
                ko.applyBindings(me.viewModelTree, container.find("div[data-id='treeContainer']")[0]);
                ko.applyBindings(me.additionalviewModel, container.find("div[data-id='additionalFormButtons']")[0]);
                ko.applyBindings(me.viewModelExceptionReport, container.find("div[data-id='exceptionReportContainer']")[0]);
                ko.applyBindings(me.viewModelFinishAppointment, container.find("div[data-id='finishAppointmentContainer']")[0]);

                me.addFormDialog.render(container);
                deferred.resolve();
            });
        });

        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        if (params != null && params.length === 1) {
            this.appointmentID = params[0];
        } else {
            this.appointmentID = 0;
        }
        return this.loadRecord(this.appointmentID);
    }

    public haveFieldsChanged(): boolean {
        // TODO - fix this
        return false;
    }

    public height(height: number): void {
        let detailHeight: number;
        if (Global.app().hideMenu) {
            detailHeight = height;
        } else {
            detailHeight = height - 68;
        }
        if (detailHeight > 0) {
            // set the min height of the detail area, so the containing box always extends to the bottom of the visible page
            this.container.find("div[data-id='treeContainer']").height(detailHeight);
            this.container.find("div[data-id='formContainer']").height(detailHeight);
            this.container.find("div[data-id='additionalFormContainer']").find(".tree-detail-container").css("min-height", detailHeight);
            this.container.find("div[data-id='finishAppointmentContainer']").height(detailHeight);
        }
    }

    public appRouterID(params: any[]): string {
        let routerID: string = "Appointment";
        if (params != null && params.length === 1) {
            routerID += params[0];
        }
        return routerID;
    }

    public title(): string {
        return Constants.LABEL_APPOINTMENT;
    }

    public subTitle(): string {
        let subTitle: string = "";
        if (this.appointmentID == null) {
            subTitle = "New";
        } else {
            subTitle = this.viewModelAppointmentDetail.TargetShortDescription();
        }
        return subTitle;
    }

    public showSaveButton(): boolean {
        // Hide the Save button if the appointment is not open
        if (this.viewModelFinishAppointment) {
            return this.viewModelFinishAppointment.isOpen();
        } else {
            return false;
        }
    }

    // -------------------------------------------------------------------------
    // create view model methods
    // -------------------------------------------------------------------------
    private createViewModelExceptionReport(): IViewModelExceptionReport {
        return {
            ExceptionNavigations: ko.observableArray<AppointmentData.IAppointmentExceptionReportNavigation>(),
            AppointmentLabel: ko.observable(Constants.LABEL_APPOINTMENT),
            QuestionLabel: ko.observable(Constants.LABEL_QUESTION),
        };
    }

    private createViewModelTree(): IViewModelTree {
        const me: TaskEntryController = this;

        return {
            Navigations: ko.observableArray<INavigationObservable>(),
            onEditNavigation(navigationObservable: INavigationObservable): void {
                me.confirmDetailChange(() => {
                    me.editNavigation(navigationObservable);
                });
            },
            onShowNavigationReport(navigationObservable: INavigationObservable): void {
                me.confirmDetailChange(() => {
                    me.showNavigationReport(navigationObservable);
                });
            },
            onShowFinishAppointment(navigationObservable: INavigationObservable): void {
                me.confirmDetailChange(() => {
                    me.showFinishAppointment(navigationObservable);
                });
            },
            onAddNavigationList(navigationObservable: INavigationObservable): void {
                me.confirmDetailChange(() => {
                     me.addFormDialog.show(navigationObservable.ListItems(),
                        (listName: string, templateFormID: number, ID: number) => {
                            me.createNewForm(ID, listName, templateFormID, navigationObservable.ID())
                                .done((listObservable: IListObservable) => {
                                        navigationObservable.Lists.push(listObservable);
                                        me.editList(listObservable);
                                });
                            });
                });
            },
            onNavigationShowActions(navigationObservable: INavigationObservable): void {
                navigationObservable.ShowActions(true);
            },
            onNavigationHideActions(navigationObservable: INavigationObservable): void {
                navigationObservable.ShowActions(false);
            },
            onEditList(listObservable: IListObservable): void {
                me.confirmDetailChange(() => {
                    me.editList(listObservable);
                });
            },
            onNavigationToggleExpanded(navigationObservable: INavigationObservable): void {
                navigationObservable.IsExpanded(!navigationObservable.IsExpanded());
            },
            onAddList(listObservable: IListObservable): void {
                me.addFormDialog.show(listObservable.ListItems(), (listName: string, templateFormID: number, ID: number) => {
                    me.createNewForm(ID, listName, templateFormID, listObservable.NavigationID())
                        .done((newListObservable: IListObservable) => {
                            listObservable.Children.push(newListObservable);
                        });
                });
            },
            onMoveListUp(listObservable: IListObservable): void {
                me.moveListItem(listObservable, true);
            },
            onMoveListDown(listObservable: IListObservable): void {
                me.moveListItem(listObservable, false);
            },
            onListToggleExpanded(listObservable: IListObservable): void {
                listObservable.IsExpanded(!listObservable.IsExpanded());
            },
            onListShowActions(listObservable: IListObservable): void {
                listObservable.ShowActions(true);
            },
            onListHideActions(listObservable: IListObservable): void {
                listObservable.ShowActions(false);
            },
        };
    }

    private createViewModelAppointmentDetail(): IViewModelAppointmentDetail {
        const app: IApplication = Global.app();

        let languages: ILanguage[] = [];
        if (app.userSession && app.userSession.Languages) {
            languages = app.userSession.Languages;
        }

        const viewModel: IViewModelAppointmentDetail = {
            TargetShortDescription: ko.observable<string>(""),
            LinkedCustomerDocuments: ko.observableArray(),
            CustomerDocumentDownloadUrl: app.webServiceBaseURL + "/api/DocumentLibrary/Download/",
            LanguageID: ko.observable(0),
            Languages: languages,
        };

        return viewModel;
    }

    private createViewModelFinishAppointment(): IViewModelFinishAppointment {
        const me = this;
        const viewModel: IViewModelFinishAppointment = {
            appointmentLabel: Constants.LABEL_APPOINTMENT,
            finishButtonLabel: `Finish ${Constants.LABEL_APPOINTMENT}`,
            isOpen: ko.observable<boolean>(true),
            isCancelled: ko.observable(false),
            hasValidationErrors: ko.observable(false),
            navigationErrors: ko.observableArray([]),
            onFinishAppointment: () => {
                UI.showConfirmDialog(
                    `Are you sure you want to finish this ${Constants.LABEL_APPOINTMENT}?  The ${Constants.LABEL_APPOINTMENT} cannot be changed once it is finished.`,
                () => {
                    const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);
                    AppointmentData.CompleteAppointment(me.appointmentID)
                        .done((response: IResponse<boolean>) => {
                        if (response.Success) {
                            UI.showNotificationMessage(`The ${Constants.LABEL_APPOINTMENT} has been finished.`, UI.messageTypeValues.success);
                            Global.app().pageManager().closeAllAndGotoDefaultPage();
                        } else {
                            UI.showErrorMessage(response.ErrorMessages);
                        }
                    })
                    .always(() => {
                        UI.hideWaitIndicator(me.container, waitContainer);
                    });
                });
            },
            onEditNavigationAfterFailedValidation: (model: AppointmentData.IAppointmentValidationErrors) => {
                const navObservable = me.viewModelTree.Navigations().find((x) => x.ID() === model.NavigationID)!;
                me.editNavigation(navObservable)
                    .done(() => {
                        me.formControl.isValid();
                    });
            },
            onCancelAppointment: () => {
                UI.showConfirmDialog(
                    `Are you sure you want to cancel this ${Constants.LABEL_APPOINTMENT}?`,
                () => {
                    const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);
                    AppointmentData.CancelAppointment(me.appointmentID)
                    .done((response: IResponse<any>) => {
                        if (response.Success) {
                            UI.showNotificationMessage(`The ${Constants.LABEL_APPOINTMENT} is now cancelled.`, UI.messageTypeValues.success);
                            Global.app().pageManager().closeAllAndGotoDefaultPage();
                        } else {
                            UI.showErrorMessage(response.ErrorMessages);
                        }
                    })
                    .always(() => {
                        UI.hideWaitIndicator(me.container, waitContainer);
                    });
                });
            },
            onOpenAppointment: () => {
                UI.showConfirmDialog(
                    `Are you sure you want to open this ${Constants.LABEL_APPOINTMENT}?`,
                () => {
                    const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);
                    AppointmentData.OpenAppointment(me.appointmentID)
                    .done((response: IResponse<any>) => {
                        if (response.Success) {
                            UI.showNotificationMessage(`The ${Constants.LABEL_APPOINTMENT} has been opened.`, UI.messageTypeValues.success);
                            me.viewModelFinishAppointment.isCancelled(false);
                            me.viewModelFinishAppointment.isOpen(true);
                            me.validateAppointment();
                        } else {
                            UI.showErrorMessage(response.ErrorMessages);
                        }
                    })
                    .always(() => {
                        UI.hideWaitIndicator(me.container, waitContainer);
                    });
                });
            },
        };

        return viewModel;
    }
    // -------------------------------------------------------------------------
    // load/update view model methods
    // -------------------------------------------------------------------------

    private validateAppointment(): JQueryPromise<any> {
        const me: TaskEntryController = this;
        const deferred: JQuery.Deferred<any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        AppointmentData.ValidateAppointment(me.appointmentID)
            .done((response: IResponse<AppointmentData.IAppointmentValidationErrorsResponse>) => {
                if (response.Success) {
                    // update UI to show error messages foreach navigation
                    me.displayNavigationErrors(response.Payload.NavigationErrors);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve();
            });

        return deferred.promise();
    }

    private displayNavigationErrors(navErrors: AppointmentData.IAppointmentValidationErrors[]) {
            this.viewModelFinishAppointment.hasValidationErrors(navErrors.length > 0);
            this.viewModelFinishAppointment.navigationErrors(navErrors);
    }

    private loadAdditionalFormItemsByTemplateFormID(templateFormID: number, appointmentID: number,
        origFormItemID?: number): JQueryPromise<any> {
        const me: TaskEntryController = this;
        const deferred: JQuery.Deferred<any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        AppointmentData.GetAdditionalFormItemsByTemplateFormID(templateFormID, appointmentID,
            this.navigationID, origFormItemID, this.listID)
            .done((response: IResponse<AppointmentData.IGetAdditionalFormItemResponse>) => {
                if (response.Success) {
                    me.updateAdditionalFormItems(response.Payload.AppointmentFormItem);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve();
            });

        return deferred.promise();
    }

    private loadFormItemsByTemplateFormID(templateFormID: number | null, appointmentID: number,
        origFormItemID?: number): JQueryPromise<any> {
        const me: TaskEntryController = this;
        const deferred: JQuery.Deferred<any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        AppointmentData.GetFormItemsByTemplateFormID(templateFormID, appointmentID, this.navigationID, origFormItemID, this.listID)
            .done((response: IResponse<AppointmentData.IGetFormItemResponse>) => {
                if (response.Success) {
                    me.updateFormItems(response.Payload.AppointmentFormItem);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve();
            });

        return deferred.promise();
    }

    private updateAdditionalFormItems(appointmentFormItems: AppointmentData.IAppointmentFormItem[]): void {
        const formItems: AppointmentData.IAppointmentFormItem[] = appointmentFormItems.map((formItem) => {
            const formItemCopy: AppointmentData.IAppointmentFormItem = {
                ID: formItem.ID,
                TemplateFormItemID: formItem.TemplateFormItemID,
                FormAnswer: formItem.FormAnswer,
                Comments: formItem.Comments,
                Documents: formItem.Documents,
                AdditionalFormItems: formItem.AdditionalFormItems,
                ControlTypeID: formItem.ControlTypeID,
                AppointmentID: formItem.AppointmentID,
                FormText: formItem.FormText,
                FormTextTranslations: formItem.FormTextTranslations,
                SortOrder: formItem.SortOrder,
                IsRequired: formItem.IsRequired,
                IsReadOnly: formItem.IsReadOnly,
                IsVisible: formItem.IsVisible,
                Fields: formItem.Fields,
                AppointmentControlTypeAttribute: formItem.AppointmentControlTypeAttribute,
                AppointmentFormItemDocumentTypesAllowed: formItem.AppointmentFormItemDocumentTypesAllowed,
                TemplatePhraseID: formItem.TemplatePhraseID,
                TemplatePhrases: formItem.TemplatePhrases,
                AdditionalTemplateFormID: formItem.AdditionalTemplateFormID,
                IsAdditionalForm: true,
                AppointmentFormItemLinkedCustomerDocuments: formItem.AppointmentFormItemLinkedCustomerDocuments,
                MultipleAdditionalForms: formItem.MultipleAdditionalForms,
                Targets: formItem.Targets,
            };
            return formItemCopy;
        });

        const loadParams: FormDataEntry.ILoadParams = {
            FormItems: formItems,
            LanguageID: this.viewModelAppointmentDetail.LanguageID(),
        };

        this.additionalFormControl.load(loadParams);
        this.additionalFormControl.show();
        this.showAdditionalFormDialog();
    }

    private updateExceptionReport(items: AppointmentData.IAppointmentExceptionReport): void {
        this.viewModelExceptionReport.ExceptionNavigations(items.ExceptionReportNavigations);
    }

    private updateFormItems(appointmentFormItems: AppointmentData.IAppointmentFormItem[]): void {
        const formItems: AppointmentData.IAppointmentFormItem[] = appointmentFormItems.map((formItem: AppointmentData.IAppointmentFormItem) => {
            const formItemCopy: AppointmentData.IAppointmentFormItem = {
                ID: formItem.ID,
                TemplateFormItemID: formItem.TemplateFormItemID,
                FormAnswer: formItem.FormAnswer,
                Comments: formItem.Comments,
                Documents: formItem.Documents,
                AdditionalFormItems: formItem.AdditionalFormItems,
                ControlTypeID: formItem.ControlTypeID,
                AppointmentID: formItem.AppointmentID,
                FormText: formItem.FormText,
                FormTextTranslations: formItem.FormTextTranslations,
                SortOrder: formItem.SortOrder,
                IsRequired: formItem.IsRequired,
                IsReadOnly: formItem.IsReadOnly,
                IsVisible: formItem.IsVisible,
                Fields: formItem.Fields,
                AppointmentControlTypeAttribute: formItem.AppointmentControlTypeAttribute,
                AppointmentFormItemDocumentTypesAllowed: formItem.AppointmentFormItemDocumentTypesAllowed,
                TemplatePhraseID: formItem.TemplatePhraseID,
                TemplatePhrases: formItem.TemplatePhrases,
                AdditionalTemplateFormID: formItem.AdditionalTemplateFormID,
                AppointmentFormItemLinkedCustomerDocuments: formItem.AppointmentFormItemLinkedCustomerDocuments,
                MultipleAdditionalForms: formItem.MultipleAdditionalForms,
                Targets: formItem.Targets,
            };

            return formItemCopy;
        });

        const loadParams: FormDataEntry.ILoadParams = {
            FormItems: formItems,
            LanguageID: this.viewModelAppointmentDetail.LanguageID(),
        };

        this.formControl.load(loadParams);
    }

    private loadRecord(id: number): JQueryPromise<any> {
        const me: TaskEntryController = this;
        const deferred: JQuery.Deferred<any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);
        AppointmentData.GetMaster(id)
            .done((response: IResponse<AppointmentData.IAppointmentMaster>) => {
                if (response.Success) {
                    me.updateViewModel(response.Payload);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve();
            });

        return deferred.promise();
    }

    private updateViewModel(master: AppointmentData.IAppointmentMaster): void {
        this.updateViewModelTree(master.Appointment);
        this.updateViewModelAppointmentDetail(master);
        this.updateViewModelFinishAppointment(master);

        if (this.viewModelTree.Navigations().length > 0) {
            // Show the first navigation by default
            this.editNavigation(this.viewModelTree.Navigations()[0]);
        }
    }

    private updateViewModelTree(appointment: AppointmentData.IAppointment): void {
        const me: TaskEntryController = this;

        this.viewModelTree.Navigations.removeAll();

        const hasVisibleFormItems = (navigation: AppointmentData.IAppointmentNavigation): boolean => {
            if (navigation.NavigationTypeID === TemplateData.NAVIGATIONTYPE_FORM) {
                return navigation.Forms && navigation.Forms.some((x) => x.IsVisible);
            } else {
                return true;
            }
        };

        appointment.Navigations.forEach((navigation: AppointmentData.IAppointmentNavigation) => {
            if (hasVisibleFormItems(navigation)) {
                me.viewModelTree.Navigations.push(me.createNavigationObservable(navigation));
            }
        });

        me.viewModelTree.Navigations.push(this.createNavigationObservable({
            ID: 0,
            Lists: [],
            Forms: [],
            NavigationTypeID: this.NAVIGATIONTYPE_FINISH_TASK,
            TemplateFormID: null,
            NavigationName: "Finish " + Constants.LABEL_APPOINTMENT,
            NavigationNameTranslations: [],
            NavigationIcon: null,
            SortOrder: 0,
            ListItems: [],
        }));

    }

    private createNavigationObservable(navigation: AppointmentData.IAppointmentNavigation): INavigationObservable {
        const me: TaskEntryController = this;
        const navigationObservable: INavigationObservable = {
            ID: ko.observable<number>(navigation.ID),
            TemplateFormID: ko.observable<number>(navigation.TemplateFormID),
            NavigationTypeID: ko.observable<number>(navigation.NavigationTypeID),
            NavigationName: ko.pureComputed(() => {
                // Find the translation for the currently selected language
                if (navigationObservable.NavigationNameTranslations) {
                    const translations = navigationObservable.NavigationNameTranslations
                            .filter((navigationNameTranslations) => navigationNameTranslations.LanguageID === me.viewModelAppointmentDetail.LanguageID());
                    if (translations && translations.length > 0 && translations[0].TranslatedText) {
                        return translations[0].TranslatedText;
                    } else {
                        return navigationObservable.NavigationNameDefault();
                    }
                } else {
                    return navigationObservable.NavigationNameDefault();
                }
            }),
            NavigationNameDefault: ko.observable<string>(navigation.NavigationName),
            NavigationNameTranslations: navigation.NavigationNameTranslations,
            NavigationIcon: ko.observable<string>(navigation.NavigationIcon),
            SortOrder: ko.observable<number>(navigation.SortOrder),
            IsExpanded: ko.observable<boolean>(true),
            ListItems: ko.observableArray<AppointmentData.IAppointmentListItem>(navigation.ListItems),
            Lists: ko.observableArray<IListObservable>(this.createListObservables(navigation.Lists, navigation.ID)),
            Forms: navigation.Forms,
            IsSelected: ko.observable<boolean>(false),
            ShowActions: ko.observable<boolean>(false),
        };

        navigationObservable.typeIsForm = ko.pureComputed((): boolean => {
            return navigationObservable.NavigationTypeID() === TemplateData.NAVIGATIONTYPE_FORM;
        });

        navigationObservable.typeIsList = ko.pureComputed((): boolean => {
            return navigationObservable.NavigationTypeID() === TemplateData.NAVIGATIONTYPE_LIST;
        });

        navigationObservable.typeIsReport = ko.pureComputed((): boolean => {
            return navigationObservable.NavigationTypeID() === TemplateData.NAVIGATIONTYPE_REPORT;
        });

        navigationObservable.typeIsFinishTask = ko.pureComputed((): boolean => {
            return navigationObservable.NavigationTypeID() === me.NAVIGATIONTYPE_FINISH_TASK;
        });

        navigationObservable.showFinishTask = ko.pureComputed((): boolean => {
            return navigationObservable.NavigationTypeID() === me.NAVIGATIONTYPE_FINISH_TASK && !Global.app().hideMenu;
        });

        navigationObservable.numberOfLists = ko.pureComputed((): number => {
            return navigationObservable.Lists().length;
        });

        navigationObservable.rowStyle = ko.pureComputed((): string => {
            if (navigationObservable.IsSelected()) {
                return "list-group-item active";
            } else {
                return "list-group-item";
            }
        });

        return navigationObservable;
    }

    private updateViewModelAppointmentDetail(appointmentMaster: AppointmentData.IAppointmentMaster): void {
        const appointment: IViewModelAppointmentDetail = this.viewModelAppointmentDetail;

        const docList: ILinkedCustomerDocument[] = [];
        for (const doc of appointmentMaster.Appointment.LinkedCustomerDocuments) {
            docList.push({
                CustomerDocumentID: doc.CustomerDocumentID,
                Description: doc.Description,
                FileName: doc.FileName,
                IsActive: doc.IsActive,
            });
        }

        for (const doc of appointmentMaster.Appointment.Target.LinkedCustomerDocuments) {

            docList.push({
                CustomerDocumentID: doc.CustomerDocumentID,
                Description: doc.Description,
                FileName: doc.FileName,
                IsActive: doc.IsActive,
            });
        }

        appointment.LinkedCustomerDocuments(_.uniqWith(docList, (a, b) => a.CustomerDocumentID === b.CustomerDocumentID));
        appointment.TargetShortDescription(this.getTargetShortDescription(appointmentMaster.Appointment.Target));
        appointment.LanguageID(appointmentMaster.Appointment.LanguageID);
    }

    private updateViewModelFinishAppointment(appointmentMaster: AppointmentData.IAppointmentMaster): void {
        this.viewModelFinishAppointment.isOpen(this.isAppointmentOpen(appointmentMaster));
        this.viewModelFinishAppointment.isCancelled(this.isAppointmentCancelled(appointmentMaster));
    }

    private isAppointmentOpen(appointmentMaster: AppointmentData.IAppointmentMaster): boolean {
        const status = appointmentMaster.StatusValues.filter((statusValue) => statusValue.ID === appointmentMaster.Appointment.StatusID);
        if (status && status.length > 0) {
            return status[0].IsOpen;
        } else {
            return false;
        }
    }

    private isAppointmentCancelled(appointmentMaster: AppointmentData.IAppointmentMaster): boolean {
        const status = appointmentMaster.StatusValues.filter((statusValue) => statusValue.ID === appointmentMaster.Appointment.StatusID);
        if (status && status.length > 0) {
            return status[0].IsCancelled;
        } else {
            return false;
        }
    }

    private getTargetShortDescription(target: AppointmentData.IAppointmentTarget): string {
        let description: string = "";

        switch (target.TargetTemplateTypeID) {
            case TargetData.TARGETEMPLTETYPEID_LOCATION:
                description = target.Address;
                break;

            case TargetData.TARGETEMPLTETYPEID_CUSTOM:
                if (target.FormItems.length > 0) {
                    description = target.FormItems[0].Value;
                }
                break;
        }

        return description;
    }

    private createListObservables(appointmentLists: AppointmentData.IAppointmentList[], navigationID: number): IListObservable[] {
        const me: TaskEntryController = this;
        const listObservables: IListObservable[] = [];

        appointmentLists.forEach((appointmentList: AppointmentData.IAppointmentList) => {
            listObservables.push(me.createListObservable(appointmentList, navigationID));
        });

        return listObservables;
    }

    private createListObservable(appointmentList: AppointmentData.IAppointmentList, navigationID: number): IListObservable {
        const listObservable: IListObservable = {
            ID: ko.observable<number>(appointmentList.ID),
            NavigationID: ko.observable<number>(navigationID),
            ListName: ko.observable<string>(appointmentList.ListName),
            HasForm: ko.observable<boolean>(appointmentList.HasForm),
            HasAttributes: ko.observable<boolean>(appointmentList.HasAttributes),
            SortOrder: ko.observable<number>(appointmentList.SortOrder),
            CanDelete: ko.observable<boolean>(appointmentList.CanDelete),
            IsActive: ko.observable<boolean>(appointmentList.IsActive),
            Children: ko.observableArray<IListObservable>(this.createListObservables(appointmentList.Children, navigationID)),
            ListItems: ko.observableArray<AppointmentData.IAppointmentListItem>(appointmentList.ListItems),
            ListForm: appointmentList.ListForm,
            TemplateFormID: ko.observable<number>(appointmentList.TemplateFormID),
            IsExpanded: ko.observable<boolean>(true),
            IsSelected: ko.observable<boolean>(false),
            ShowActions: ko.observable<boolean>(false),
        };

        listObservable.typeIsForm = ko.pureComputed((): boolean => {
            return listObservable.HasForm();
        });

        listObservable.typeIsList = ko.pureComputed((): boolean => {
            return !listObservable.HasForm();
        });

        listObservable.numberOfLists = ko.pureComputed((): number => {
            return listObservable.Children().length;
        });

        listObservable.rowStyle = ko.pureComputed((): string => {
            if (listObservable.IsSelected()) {
                return "tree-item tree-item-selected";
            } else {
                return "tree-item";
            }
        });

        return listObservable;
    }

    // -------------------------------------------------------------------------
    // FInish Appointment
    // -------------------------------------------------------------------------

    private showFinishAppointment(navigationObservable: INavigationObservable): void {
        // shows a map that shows the points logged during the inspection
        this.hideExceptionReportContainer();
        this.hideFormContainer();
        this.showFinishAppointmentContainer();

        // logic for displaying and handling navigation errors
        if (this.viewModelFinishAppointment.isOpen()) {
            this.validateAppointment();
        }

        this.formDetail = false;
        this.listID = null;
        this.templateFormID = null;

        // update the UI
        this.unselectAllNavigation();
        navigationObservable.IsSelected(true);
    }

    // -------------------------------------------------------------------------
    // navigation edit functions
    // -------------------------------------------------------------------------

    // loads the form associated with the navigation item into the
    // detail section for editing
    private editNavigation(navigationObservable: INavigationObservable): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any> = $.Deferred();
        // show the form associated with the navigation item
        this.navigationID = navigationObservable.ID();
        this.templateFormID = navigationObservable.TemplateFormID();
        this.listID = null;
        this.hideExceptionReportContainer();
        this.hideFinishAppointmentContainer();

        this.formDetail = true;

        this.loadFormItemsByTemplateFormID(navigationObservable.TemplateFormID(), this.appointmentID)
            .done(() => {
                deferred.resolve();
            });
        this.showFormContainer();
        this.formControl.show();

        // update the UI
        this.unselectAllNavigation();
        navigationObservable.IsSelected(true);
        return deferred.promise();
    }

    // shows the report associated with the navigation item
    private showNavigationReport(navigationObservable: INavigationObservable): JQuery.Promise<any, any, any> {
        // load report data
        const me: TaskEntryController = this;
        const deferred: JQuery.Deferred<any> = $.Deferred();
        const waitContainer: JQuery = UI.showWaitIndicator(me.container);

        this.hideFormContainer();
        this.showExceptionReportContainer();

        this.formDetail = false;

        AppointmentData.GetExceptionReportData(me.appointmentID)
            .done((response: IResponse<AppointmentData.IAppointmentExceptionReport>) => {
                if (response.Success) {
                    // display report data
                    me.updateExceptionReport(response.Payload);

                    deferred.resolve();
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                    deferred.reject();
                }
            })
            .fail(() => {
                deferred.reject();
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });

        // update the UI
        this.unselectAllNavigation();
        navigationObservable.IsSelected(true);

        return deferred.promise();
    }

    // -------------------------------------------------------------------------
    // list editing functions
    // -------------------------------------------------------------------------

    // loads the specified list item into the detail screen for editing
    private editList(listObservable: IListObservable): void {

        this.hideExceptionReportContainer();
        this.hideFinishAppointmentContainer();
        this.showFormContainer();
        this.listID = listObservable.ID();
        this.formDetail = true;
        this.navigationID = listObservable.NavigationID();
        this.templateFormID = listObservable.TemplateFormID();
        this.loadFormItemsByTemplateFormID(listObservable.TemplateFormID(), this.appointmentID);
        // this.formControl.load(loadParams);
        this.formControl.show();

        // update the UI
        this.unselectAllNavigation();
        listObservable.IsSelected(true);
    }

    private moveListItem(listObservableToMove: IListObservable, moveUp: boolean): void {
        // a list item can be under a Navigation item, or under a List item
        // need to figure out which one, then do the move

        // check navigation items first
        const me: TaskEntryController = this;
        let i: number = 1;
        let itemFound: boolean = false;

        this.viewModelTree.Navigations().forEach((navigation: INavigationObservable) => {
            if (itemFound) { return; }

            let index: number = navigation.Lists.indexOf(listObservableToMove);
            if (index >= 0) {
                // item is under this navigation item
                navigation.Lists.remove(listObservableToMove);
                if (moveUp) {
                    index = index - 1;

                } else {
                    index = index + 1;
                }

                navigation.Lists.splice(index, 0, listObservableToMove);
                navigation.Lists().forEach((list: IListObservable) => {
                    list.SortOrder(i);
                    i++;
                });

                // save new list order
                me.saveListOrder(navigation.Lists());

                listObservableToMove.ShowActions(false);
                itemFound = true;
            } else {
                // check any children
                navigation.Lists().forEach((list: IListObservable) => {
                    if (itemFound) { return; }
                    if (list.Children().length === 0) { return; }
                    itemFound = me.moveListItemForList(listObservableToMove, moveUp, list);
                });
            }
        });
    }

    private moveListItemForList(listObservableToMove: IListObservable, moveUp: boolean, parentList: IListObservable): boolean {
        const me: TaskEntryController = this;
        let i: number = 1;
        let itemFound: boolean = false;

        let index: number = parentList.Children.indexOf(listObservableToMove);
        if (index >= 0) {
            // found the item, so move it
            parentList.Children.remove(listObservableToMove);
            if (moveUp) {
                index = index - 1;
            } else {
                index = index + 1;
            }
            parentList.Children.splice(index, 0, listObservableToMove);
            parentList.Children().forEach((list: IListObservable) => {
                list.SortOrder(i);
                i++;
            });

            // save new list order
            me.saveListOrder(parentList.Children());

            listObservableToMove.ShowActions(false);
            return true;
        }

        // check any children lists
        parentList.Children().forEach((child: IListObservable) => {
            if (itemFound) { return; }
            if (child.Children().length === 0) { return; }

            itemFound = me.moveListItemForList(listObservableToMove, moveUp, child);
        });

        return itemFound;
    }

    // -------------------------------------------------------------------------
    // add form functions
    // -------------------------------------------------------------------------
    private createNewForm(ID: number, listName: string, templateFormID: number, navigationID: number): JQueryPromise<IListObservable> {
        const me: TaskEntryController = this;
        const deferred: JQuery.Deferred<IListObservable> = $.Deferred();

        // web service call to copy new list form to database
        AppointmentData.SaveNewListForm(listName, ID, me.appointmentID, navigationID)
            .done((response: IResponse<AppointmentData.IAppointmentList>) => {
                if (response.Success) {
                    me.listID = response.Payload.ID;
                    const appointmentList: AppointmentData.IAppointmentList = {
                        ID: response.Payload.ID,
                        ParentID: response.Payload.ParentID,
                        NavigationID: navigationID,
                        Children: [], // forms never have children
                        AttributeForm: [], // attribute forms currently not supported
                        TemplateFormID: templateFormID,
                        ListForm: me.getAppointmentFormItems(response.Payload.ListForm), // the items on this form
                        ListName: listName,
                        IsActive: true,
                        HasForm: true,
                        HasAttributes: false, // attribute forms currently not supported
                        SortOrder: 0, // this will be set by the web service
                        CanDelete: true, // user can delete stuff they have added
                        ListItems: [], // not used for forms
                    };
                    const listObservable: IListObservable = me.createListObservable(appointmentList, navigationID);
                    deferred.resolve(listObservable);

                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                    deferred.reject();
                }
            })
            .fail(() => {
                deferred.reject();
            });
        return deferred.promise();
    }

    private getAppointmentFormItems(FormItems: AppointmentData.IAppointmentFormItem[]): AppointmentData.IAppointmentFormItem[] {
        const formItems: AppointmentData.IAppointmentFormItem[] = [];
        const me: TaskEntryController = this;

        FormItems.forEach((FormItem: AppointmentData.IAppointmentFormItem) => {
            const formItem: AppointmentData.IAppointmentFormItem = {
                ID: FormItem.ID,
                TemplateFormItemID: FormItem.ID,
                AppointmentID: me.appointmentID,
                FormAnswer: FormItem.FormAnswer, // answers, will be entered by the user
                Comments: [],  // comments, will be entered by the user
                Documents: [],
                AdditionalFormItems: [], // will be populated if the user opens the additional form
                ControlTypeID: FormItem.ControlTypeID,
                FormText: FormItem.FormText,
                SortOrder: FormItem.SortOrder,
                IsRequired: FormItem.IsRequired,
                IsReadOnly: FormItem.IsReadOnly,
                IsVisible: FormItem.IsVisible,
                Fields: FormItem.Fields,
                AppointmentControlTypeAttribute: FormItem.AppointmentControlTypeAttribute,
                AppointmentFormItemDocumentTypesAllowed: FormItem.AppointmentFormItemDocumentTypesAllowed,
                TemplatePhraseID: FormItem.TemplatePhraseID,
                AdditionalTemplateFormID: FormItem.AdditionalTemplateFormID,
                MultipleAdditionalForms: FormItem.MultipleAdditionalForms,
                Targets: FormItem.Targets,
            };
            formItems.push(formItem);
        });

        return formItems;
    }

    // -------------------------------------------------------------------------
    // uI functions
    // -------------------------------------------------------------------------

    // this function is called when the user is changing what is
    // displayed in the detail section (navigation or list) .
    // If the data is valid and as changed, will save the data
    // If the data is not valid, will not let the user move to the
    //  next page and shows an error message.
    private confirmDetailChange(onConfirmed: () => void): void {
        if (!this.viewModelFinishAppointment.isOpen() ||
            !this.formDetail) {
            // If this task is not open, then we don't need to save
            // or do any form validation
            // Or if user is not a form (like the report page or finish page)
            // the nothing to save
            onConfirmed();
            return;
        }

        // User is on a form, auto save data if we can
        const showFieldValidationError = () => {
            UI.showErrorMessage(["There are errors in this section.  Please correct before moving to another section."]);
        };

        if (this.detailsChanged()) {
            this.save(false)
                .done(() => {
                    onConfirmed();
                })
                .fail(() => {
                    showFieldValidationError();
                });
        }  else {
            onConfirmed();
        }
    }

    private unselectAllNavigation(): void {
        const me: TaskEntryController = this;

        this.viewModelTree.Navigations().forEach((navigation: INavigationObservable) => {
            navigation.IsSelected(false);
            if (navigation.Lists().length > 0) {
                me.unselectAllLists(navigation.Lists);
            }
        });
    }

    private unselectAllLists(lists: KnockoutObservableArray<IListObservable>): void {
        const me: TaskEntryController = this;

        lists().forEach((list: IListObservable) => {
            list.IsSelected(false);
            if (list.Children().length > 0) {
                me.unselectAllLists(list.Children);
            }
        });
    }

    private detailsChanged(): boolean {
        const formItemsChanged: boolean = this.formControl.haveFieldsChanged();

        if (this.formDetail && formItemsChanged) {
                return true;
            }
        return false;
    }

    private showFormContainer(): void {
        this.container.find("div[data-id='formContainer']").removeClass("hidden");
    }

    private hideFormContainer(): void {
        this.container.find("div[data-id='formContainer']").addClass("hidden");
    }

    private showAdditionalFormDialog(): void {
        this.container.find("div[data-id='additionalFormDialog']").modal("show");
    }

    private showExceptionReportContainer(): void {
        this.container.find("div[data-id='exceptionReportContainer']").removeClass("hidden");
    }

    private hideExceptionReportContainer(): void {
        this.container.find("div[data-id='exceptionReportContainer']").addClass("hidden");
    }

    private showFinishAppointmentContainer(): void {
        this.container.find("div[data-id='finishAppointmentContainer']").removeClass("hidden");
    }
    private hideFinishAppointmentContainer(): void {
        this.container.find("div[data-id='finishAppointmentContainer']").addClass("hidden");
    }

    // -------------------------------------------------------------------------
    // additional form functions
    // -------------------------------------------------------------------------
    private confirmedOpenAdditionalForm(templateFormID: number): void {
        const me: TaskEntryController = this;
        // get form items for additional form and open additional form
        const myformitems = me.formControl.getFormItems();
        const origFormItemID: number = myformitems.FormItems[me.additionalviewModel.formItemIndex()].ID;

        me.loadAdditionalFormItemsByTemplateFormID(templateFormID, this.appointmentID, origFormItemID);
    }

    private openAdditionalForm(templateFormID: number): void {
        const me: TaskEntryController = this;
        me.confirmedOpenAdditionalForm(templateFormID);
    }

    // -------------------------------------------------------------------------
    // save appointment detail functions
    // appointment detail are fields like appointment date, time,
    // assigned user, etc.
    // -------------------------------------------------------------------------
    private saveListOrder(lists: IListObservable[]): JQueryPromise<any> {
        const me: TaskEntryController = this;
        const deferred: JQuery.Deferred<any> = $.Deferred();
        let waitContainer: JQuery;

        waitContainer = UI.showWaitIndicator(me.container);
        AppointmentData.SaveListOrder(ko.toJS(lists))
            .done((response: IResponse<any>) => {
                if (response.Success) {
                    UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                    deferred.resolve();
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                    deferred.reject();
                }
            })
            .fail(() => {
                deferred.reject();
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });

        return deferred.promise();
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/taskEntry.ts